import { FC, useRef } from 'react';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Carousel, Flex, Image, Space, Typography } from 'antd';
import { CarouselRef } from 'antd/lib/carousel';
import { useTranslation } from 'react-i18next';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';
import { useAuthStateContext } from 'providers/Auth';

import {
  StyledModal,
  StyledModalContent,
  StyledTitle,
  AvatarContainer,
  SignatureContainer
} from './styled';
import PreviewHeader from '../PreviewHeader';
import { getFileUrl } from 'utils/file';

const { Text } = Typography;

const ReportPreviewComponent: FC = () => {
  const { t } = useTranslation();
  const { reportPreview } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();

  const carouselRef = useRef<CarouselRef>(null);
  const { user: meData } = useAuthStateContext();

  const doctorNameHeader = reportPreview.params?.appointmentId
    ? ''
    : `${meData?.first_name} ${meData?.last_name}`;

  const lng = reportPreview.params?.lang || 'en';

  const documentFooter = reportPreview.params?.appointmentId ? (
    <span
      style={{
        textAlign: 'center',
        fontSize: 12,
        color: '#0B1A30',
        fontWeight: 500
      }}
      dangerouslySetInnerHTML={{
        __html: t('reports.medicus_info', { lng })
      }}
    />
  ) : (
    <span
      style={{
        textAlign: 'center',
        fontSize: 12,
        color: '#0B1A30',
        fontWeight: 500
      }}
    >
      {t('reports.powered', { lng })}
    </span>
  );

  return (
    <StyledModal
      open
      centered
      footer={null}
      width={600}
      onCancel={() => toggleModal('reportPreview', false)}
      closeIcon={
        <CloseOutlined style={{ color: 'var(--white)', fontSize: 32 }} />
      }
    >
      <Carousel infinite={false} dots={false} arrows ref={carouselRef}>
        {!!reportPreview.params?.appointmentId && (
          <StyledModalContent>
            <Space direction="vertical" size={66}>
              <PreviewHeader
                lng={lng}
                date={reportPreview.params?.date}
                doctor_name={doctorNameHeader}
              />
              <StyledTitle>{t('reports.conclusion', { lng })}</StyledTitle>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('reports.conclusion_info', { lng })
                }}
              ></span>
            </Space>
            {documentFooter}
          </StyledModalContent>
        )}
        <StyledModalContent>
          <Space direction="vertical" size={66}>
            <PreviewHeader
              lng={lng}
              date={reportPreview.params?.date}
              doctor_name={doctorNameHeader}
            />
            <div>
              <StyledTitle>{t('reports.about_doctor', { lng })}</StyledTitle>
              <AvatarContainer style={{ paddingTop: 35 }}>
                <Image src={getFileUrl(meData?.avatar)} preview={false} />
                <Typography.Text>
                  {meData?.first_name} {meData?.last_name}
                </Typography.Text>
                <span
                  dangerouslySetInnerHTML={{
                    __html: reportPreview.params?.aboutDoctor as string
                  }}
                />
              </AvatarContainer>
            </div>
          </Space>
          {documentFooter}
        </StyledModalContent>
        <StyledModalContent>
          <PreviewHeader
            lng={lng}
            date={reportPreview.params?.date}
            doctor_name={doctorNameHeader}
          />
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <StyledTitle>{t('reports.report', { lng })}</StyledTitle>
            <Text style={{ paddingTop: 12 }}>
              <Text style={{ fontWeight: 'bold' }}>
                {t('reports.patient', { lng })}
              </Text>
              {` ${reportPreview.params?.patientName || ''} ${
                reportPreview.params?.patientLastName || ''
              } (${t('reports.dob', { lng })}: ${
                reportPreview.params?.patientBirth
                  ? dayjs(reportPreview.params?.patientBirth).format(
                      DATE_FORMAT
                    )
                  : '-'
              })`}
            </Text>
            <span
              dangerouslySetInnerHTML={{
                __html: reportPreview.params?.report || ''
              }}
            />
            {reportPreview.params?.icd?.length && (
              <Flex gap={4} vertical>
                <Text
                  style={{
                    color: '#0B1A3033',
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}
                >
                  {t('reports.report_icd')}
                </Text>
                <Flex vertical>
                  {reportPreview.params?.icd.map(item => (
                    <Text key={item.id + item.title}>{item.title}</Text>
                  ))}
                </Flex>
              </Flex>
            )}
          </Space>
          <Flex align="center" gap={16} vertical>
            {!!reportPreview.params?.signature && (
              <>
                <div>
                  <SignatureContainer>
                    <Image
                      src={reportPreview.params.signature}
                      preview={false}
                    />
                  </SignatureContainer>
                  <Text
                    style={{
                      textAlign: 'center',
                      fontSize: 12,
                      color: '#0B1A30',
                      fontWeight: 700
                    }}
                  >
                    {meData?.first_name} {meData?.last_name}
                  </Text>
                </div>
                {documentFooter}
              </>
            )}
          </Flex>
        </StyledModalContent>
      </Carousel>
      <Space
        size={12}
        style={{ paddingTop: 8, justifyContent: 'center', width: '100%' }}
      >
        <Button onClick={() => carouselRef.current?.prev()}>
          <LeftOutlined />
        </Button>
        <Button onClick={() => carouselRef.current?.next()}>
          <RightOutlined />
        </Button>
      </Space>
    </StyledModal>
  );
};

const ReportPreview: FC = () => {
  const { reportPreview } = useModalStateContext();

  if (!reportPreview.visible) {
    return null;
  }

  return <ReportPreviewComponent />;
};

export default ReportPreview;
